import styled from '@emotion/styled';
import { LoadingButton } from '@mui/lab';
import { Button, FormGroup, FormLabel, Input, Select } from '@mui/material';

export const StyledContainer = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 32px;
  padding: 24px;
  max-width: 1400px;
  margin: 0 auto;

  @media (max-width: 968px) {
    grid-template-columns: 1fr;
  }
`;

export const AddressSection = styled.div`
  background: #ffffff;
  padding: 24px;
  border-radius: 8px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
`;

export const CriteriaSection = styled.div`
  background: #ffffff;
  padding: 24px;
  border-radius: 8px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
`;

export const SectionTitle = styled.h2`
  color: #484d87;
  margin-bottom: 24px;
  font-size: 20px;
  font-weight: 600;
`;

export const Form = styled.form`
  display: flex;
  flex-direction: column;
  gap: 20px;
`;

export const StyledFormGroup = styled(FormGroup)`
  margin-bottom: 16px;
`;

export const StyledLabel = styled(FormLabel)`
  display: block;
  margin-bottom: 8px;
  color: #333;
  font-weight: 500;
  font-size: 14px;
`;

export const StyledInput = styled(Input)`
  width: 100%;
  padding: 10px 12px;
  border: 1px solid #d1d5db;
  border-radius: 4px;
  font-size: 14px;

  &:before,
  &:after {
    display: none;
  }

  &:focus {
    border-color: #4a4f7f;
    outline: none;
  }

  &:disabled {
    background-color: #f3f4f6;
    cursor: not-allowed;
  }
`;

export const StyledSelect = styled(Select)`
  width: 100%;
  padding: 8px 12px;
  border: 1px solid #d1d5db;
  border-radius: 4px;
  font-size: 14px;

  &:focus {
    border-color: #4a4f7f;
  }
`;

export const StatusDropdown = styled.select`
  width: 100%;
  padding: 10px 12px;
  border: 1px solid #d1d5db;
  border-radius: 4px;
  font-size: 14px;
  background-color: #ffffff;
  cursor: pointer;

  &:focus {
    border-color: #4a4f7f;
    outline: none;
  }

  &:disabled {
    background-color: #f3f4f6;
    cursor: not-allowed;
  }
`;

export const ErrorText = styled.span`
  color: #dc2626;
  font-size: 12px;
  margin-top: 4px;
  display: block;
`;

export const SuccessMessage = styled.div`
  color: #059669;
  background-color: #d1fae5;
  padding: 12px 16px;
  border-radius: 6px;
  margin-bottom: 16px;
  font-size: 14px;
  border: 1px solid #6ee7b7;
`;

export const ErrorMessage = styled.div`
  color: #dc2626;
  background-color: #fee2e2;
  padding: 12px 16px;
  border-radius: 6px;
  margin-bottom: 16px;
  font-size: 14px;
  border: 1px solid #fca5a5;
`;

export const StyledLoadingButton = styled(LoadingButton)`
  height: 48px;
  font-size: 16px;
  margin-top: 16px;
  background-color: #4a4f7f;
  color: #ffffff;
  text-transform: none;
  font-weight: 500;

  &:hover {
    background-color: #3a3f6f;
  }

  &.MuiLoadingButton-loading {
    background-color: #4a4f7f;

    .MuiLoadingButton-loadingIndicator {
      color: #fff;
    }
  }

  &:disabled {
    background-color: #9ca3af;
  }
`;

export const CriteriaList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

export const CriteriaItem = styled.div`
  padding: 20px;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  background-color: #f9fafb;
  transition: box-shadow 0.2s;

  &:hover {
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  }
`;

export const CriteriaHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 16px;
  padding-bottom: 12px;
  border-bottom: 1px solid #e5e7eb;
`;

export const CriteriaName = styled.h3`
  color: #1f2937;
  font-size: 16px;
  font-weight: 600;
  margin: 0;
`;

export const CriteriaStatus = styled.span`
  padding: 6px 12px;
  border-radius: 16px;
  font-size: 12px;
  font-weight: 600;
  text-transform: uppercase;
  background-color: ${(props) => {
    switch (props.status) {
      case 'COMPLETED':
        return '#d1fae5';
      case 'NOT_COMPLETED':
        return '#fef3c7';
      case 'NOT_APPLICABLE':
        return '#e5e7eb';
      default:
        return '#f3f4f6';
    }
  }};
  color: ${(props) => {
    switch (props.status) {
      case 'COMPLETED':
        return '#059669';
      case 'NOT_COMPLETED':
        return '#d97706';
      case 'NOT_APPLICABLE':
        return '#6b7280';
      default:
        return '#374151';
    }
  }};
`;

export const SaveButton = styled(Button)`
  margin-top: 12px;
  background-color: #4a4f7f;
  color: #ffffff;
  padding: 10px 20px;
  text-transform: none;
  font-weight: 500;

  &:hover {
    background-color: #3a3f6f;
  }

  &:disabled {
    background-color: #9ca3af;
    color: #ffffff;
  }
`;

export const AppointmentFields = styled.div`
  margin-top: 16px;
  padding-top: 16px;
  border-top: 1px solid #e5e7eb;
`;

export const OptInSection = styled.div`
  background: #ffffff;
  padding: 24px;
  border-radius: 8px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  grid-column: 1 / -1;
`;

export const CareGapsSection = styled.div`
  background: #ffffff;
  padding: 24px;
  border-radius: 8px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
`;

export const OptInList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

export const OptInItem = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 12px;
  padding: 16px;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  background-color: #f9fafb;
  transition: all 0.2s ease;

  &:hover {
    background-color: #f3f4f6;
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);
  }
`;

export const OptInCheckbox = styled.input`
  width: 18px;
  height: 18px;
  margin-top: 2px;
  cursor: pointer;
  accent-color: #4a4f7f;

  &:focus {
    outline: 2px solid #4a4f7f;
    outline-offset: 2px;
  }
`;

export const OptInLabel = styled.label`
  flex: 1;
  cursor: pointer;
  display: flex;
  flex-direction: column;
  gap: 4px;

  strong {
    color: #1f2937;
    font-size: 16px;
    font-weight: 600;
  }

  span {
    color: #6b7280;
    font-size: 14px;
    line-height: 1.4;
  }
`;

export const CancelButton = styled.div`
  background-color: white;
  color: #4a4f7f;
  width: 125px;
  height: 48px;
  border: 1px solid #4a4f7f;
  border-radius: 25px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  font-family: karla;
  font-size: 16px;
  font-weight: 500;
  margin-top: 16px;
  transition: background-color 0.2s;

  &:hover {
    background-color: #f3f4f6;
  }
`;
